
import Backdrop from '@mui/material/Backdrop'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import FormControlLabel from '@mui/material/FormControlLabel'
import TextField from '@mui/material/TextField'
import AdminPage from 'admin/components/AdminPage'
import * as React from 'react'
import { useDispatch } from 'react-redux'
import { NavLink } from 'react-router'
import { of as observableOf, Subject } from 'rxjs'
import { catchError, mergeMap } from 'rxjs/operators'
import { getTags, updateTag } from 'services/content/statuses/actions'
import { message } from 'services/snackbar'
import { StoreThunkDispatch } from 'store/state'
import styles from './CSTagsManager.pcss'

export function CSTagsManager() {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const [tags, setTags] = React.useState<Record<string, { id: number, name: string, active: boolean, statusIdeasCount: number }>>({})
  const [showAll, setShowAll] = React.useState(false)
  const [showActiveOnly, setShowActiveOnly] = React.useState(true)
  const [loading, setLoading] = React.useState(true)
  const [filter, setFilter] = React.useState('')

  const fetch$ = React.useRef<Subject<void>>(undefined)
  const update$ = React.useRef<Subject<{ id: number, active: boolean }>>(undefined)

  React.useEffect(() => {
    fetch$.current = new Subject()
    fetch$.current.pipe(
      mergeMap(() => {
        setLoading(true)
        return dispatch(getTags(true)).pipe(catchError(() => {
          dispatch(message('Could not fetch data! Please refresh.'))
          return observableOf([])
        }))
      }))
      .subscribe(response => {
        setTags(response.reduce((map: Record<string, any>, tag: any) => {
          map[tag.id] = tag
          return map
        }, {}))
        setLoading(false)
      })

    fetch$.current.next()

    return () => {
      fetch$.current?.unsubscribe()
    }
  }, [dispatch])

  React.useEffect(() => {
    update$.current = new Subject()
    update$.current.pipe(mergeMap(
      ({ id, active }) => dispatch(updateTag(id, active))
        .pipe(
          catchError((error: Error) => {
            dispatch(message('Error! Update failed: ' + error.message))
            return observableOf({ error: true })
          })
        )
    ))
      .subscribe((response: any) => {
        if (!response.error) {
          dispatch(message('Tag updated!'))
          setTags(current => ({
            ...current,
            [response.id]: {
              ...current[response.id],
              active: response.active
            }
          }))
        }
      })
  }, [dispatch])

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setShowAll(false)
    setShowActiveOnly(false)
    setFilter(value)
  }

  const onTagClick = (id: number) => {
    update$.current?.next({ id, active: !tags[id].active })
  }

  const toggleShowAll = () => {
    setFilter('')
    setShowActiveOnly(false)
    setShowAll(current => !current)
  }

  const toggleShowActiveOnly = () => {
    setFilter('')
    setShowAll(false)
    setShowActiveOnly(current => !current)
  }

  return (
    <AdminPage title="Tags">
      <NavLink to="/admin/product/conversation-starters">Back to Post Ideas</NavLink>
      <Backdrop open={loading} className={styles.backdrop}>
        <CircularProgress color="primary" />
      </Backdrop>
      <div className={styles['tags-wrapper']}>
        <div className={styles['search-box']}>
          <FormControlLabel
            label="Show all"
            control={
              <Checkbox checked={showAll} onChange={toggleShowAll} />
            }
            className={styles.cb}
          />
          <FormControlLabel
            label="Show active only"
            control={
              <Checkbox checked={showActiveOnly} onChange={toggleShowActiveOnly} />
            }
            className={styles.cb}
          />
          <TextField
            placeholder="Filter tags"
            value={filter}
            classes={{ root: styles['search-input'] }}
            onChange={onSearchChange}
          />
        </div>
        <div className={styles.grid}>
          {showAll && !filter && Object.values(tags).map(t => (
            <TagChip tag={t} key={t.id} onClick={onTagClick} />
          ))}
          {filter && Object.values(tags).map(t => {
            if (t.name.toLowerCase().indexOf(filter) !== -1) {
              return (
                <TagChip tag={t} key={t.id} onClick={onTagClick} />
              )
            }
            return null
          })}
          {showActiveOnly && Object.values(tags).filter(t => t.active).map(t => (
            <TagChip tag={t} key={t.id} onClick={onTagClick} />
          ))}
        </div>
      </div>
    </AdminPage>
  )
}

const TagChip = React.memo(function (props: {
  tag: { id: number, name: string, active: boolean, statusIdeasCount: number },
  onClick: (id: number) => void
}
) {
  const onClick = () => {
    props.onClick(props.tag.id)
  }

  return (
    <Chip
      size="small"
      label={`${props.tag.name} (${props.tag.statusIdeasCount})`}
      onClick={onClick}
      className={`${styles.chip} ${props.tag.active ? styles.active : ''}`}
    />
  )
})

export default CSTagsManager
